import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../../config';
import { legendTooltipFormatter } from './legendTooltip';

const rideTrendsLegendDefs: Record<string, string> = {
  'Decoupling': 'Heart rate drift relative to power over a ride. Lower = better aerobic fitness. Above 5% = zone too hard.',
  'EF': 'Efficiency Factor = normalized power / average HR. Higher = better aerobic efficiency.',
  'IF': 'Intensity Factor = normalized power / FTP. Shows how hard the ride was relative to threshold.',
};

interface RideTrendPoint {
  date: string;
  ef: number | null;
  decoupling: number | null;
  intensity_factor: number | null;
}

type ChartRange = '30d' | '90d' | '6mo' | '1yr' | 'all';

const rangeOptions: { value: ChartRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6mo', label: '6mo' },
  { value: '1yr', label: '1yr' },
  { value: 'all', label: 'All' },
];

const formatDateTick = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    const day = d.getDate();
    const mon = d.toLocaleDateString('en-US', { month: 'short' });
    const yr = String(d.getFullYear()).slice(-2);
    return `${day} ${mon} '${yr}`;
  } catch {
    return dateStr;
  }
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{formatDateTick(label)}</div>
      {payload.map((p: any) => {
        if (p.value === null || p.value === undefined) return null;
        let formatted: string;
        if (p.dataKey === 'ef') {
          formatted = `${p.value.toFixed(2)}`;
        } else if (p.dataKey === 'decoupling') {
          formatted = `${p.value.toFixed(1)}%`;
        } else if (p.dataKey === 'intensity_factor') {
          formatted = `${p.value.toFixed(2)}`;
        } else {
          formatted = String(p.value);
        }
        return (
          <div key={p.name} style={{ color: p.stroke || '#333', marginBottom: 2 }}>
            {p.name}: {formatted}
          </div>
        );
      })}
    </div>
  );
};

interface RideTrendsChartProps {
  targetUserId?: number | null;
}

const RideTrendsChart: React.FC<RideTrendsChartProps> = ({ targetUserId }) => {
  const [range, setRange] = useState<ChartRange>('90d');
  const [data, setData] = useState<RideTrendPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (r: ChartRange) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range: r });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await fetch(`${API_BASE_URL}/api/analytics/ride-trends?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load ride trends data');
      const json = await res.json();
      setData(json.points || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    fetchData(range);
  }, [range, fetchData]);

  return (
    <div className="analytics-card" style={{ marginTop: 16 }}>
      <h3>Ride Trends</h3>
      <div className="analytics-range-buttons">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="analytics-chart-loading">Loading...</div>
      ) : data.length === 0 ? (
        <div className="analytics-chart-empty">No ride trends data available</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#999' }}
              tickFormatter={(val) => formatDateTick(val)}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12, fill: '#999' }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12, fill: '#999' }}
            />
            <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              iconType="line"
              formatter={legendTooltipFormatter(rideTrendsLegendDefs)}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="ef"
              stroke="#2563eb"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3 }}
              connectNulls
              name="EF"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="decoupling"
              stroke="#dc2626"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              activeDot={{ r: 3 }}
              connectNulls
              name="Decoupling"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="intensity_factor"
              stroke="#f59e0b"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3 }}
              connectNulls
              name="IF"
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default RideTrendsChart;
